import Tabs from 'Components/Molecules/Tabs/Tabs';
import React, {useMemo, useState} from 'react';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import Table from 'Components/Molecules/Table/Table';
import Button from 'Components/Atoms/Button/Button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faEllipsisVertical} from '@fortawesome/pro-regular-svg-icons';
import DropdownItem from 'Components/Atoms/Dropdown/DropdownItem/DropdownItem';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import {fileTypesAsImage, fileTypesWithoutPreview} from 'Services/File/FileService';
import {getAccessTokenFromLocal, getInternalDatasetIdByName} from 'Services/helpers';
import {API_URL} from 'Store/Shared/Middleware/API/Constants';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import Loader from 'Components/Atoms/Loader/Loader';
import styles from 'Components/Organisms/Modules/Mails/Mail/Mail.module.sass';
import Title from 'Components/Atoms/Title/Title';
import {convertTimestampToDateAndTime} from 'Services/Date/Date';
import sanitizeHtml from 'sanitize-html';
import DeletionModal from 'Components/Molecules/Modals/Actions/DeletionModal/DeletionModal';
import { useMutation } from 'react-query';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import useFetch from 'Api/useFetch';

function stripHtmlTags(html) {
    const regex = /<[^>]*>/g;
    const strippedText = html.replace(regex, '');
    return strippedText;
}


interface Props {
    data: any
    onDelete?: any
}

const Mail = ({
    data, onDelete
}: Props) => {
    const [state, setState] = useState<any>({
        activeTab: 'overview',
        isPreviewing: false,
        isLoadingAttachmentPreview: false,
        activeAttachment: {},
    });

    const tabs = useMemo(() => {
        let tabs = [{
            id: 'overview',
            title: 'Overzicht'
        }];

        const hasAttachments = data?.attachments?.length > 0;
        if (hasAttachments) {
            tabs.push({
                id: 'attachments',
                title: 'Bijlage'
            });
        }

        if (data?.status === 'CONNECTED') {
            tabs.push({
                id: 'entities',
                title: 'Gekoppelde entiteiten'
            });
        }

        return tabs;
    }, [data]);

    const handleTabClick = (value: string) => setState((state) => ({
        ...state,
        activeTab: value
    }));

    const fetchUrl = async (attachment: any) => {
        setState((state) => ({
            ...state,
            isLoadingAttachmentPreview: true
        }));
        const response = await fetch(`${API_URL}/api/v2.9/mails/${data.id}/attachments/${attachment.id}/download`, {
            headers: {
                Authorization: `Bearer ${getAccessTokenFromLocal()}`
            }
        }).then(response => response.blob());
        const blob = new Blob([response], {type: attachment.type});

        setState((state) => ({
            ...state,
            isLoadingAttachmentPreview: false
        }));

        return window.URL.createObjectURL(blob);
    }

    const handleAttachmentClick = (attachment: any) => async () => {
        setState((state) => ({
            ...state,
            isPreviewing: true
        }));

        const url = await fetchUrl(attachment);

        setState((state) => ({
            ...state,
            activeAttachment: attachment,
            previewUrl: url
        }));
    };

    const handleDownload = (attachment: any) => async () => {
        const url = await fetchUrl(attachment);

        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    const deleteMutation = useMutation(async (id: string) => {
        const response = await fetch(`${API_URL}/api/v2.9/mails/${id}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${getAccessTokenFromLocal()}`
            }
        });

        console.log(response);

        if (!response.ok) {
            throw new Error('Failed to delete todo');
        }

        return response;
    }, {
        onSuccess: () => onDelete()
    });

    const handleDeletePopup = () => setState((state) => ({
        ...state,
        isDeletePopupOpen: true
    }));

    const processDelete = () => deleteMutation.mutate(data.id);

    const {
        isLoading: isLoadingEntities,
        data: entities
    } = useFetch(
        `/api/v2.9/mails/${data.id}/entities`,
        {},
        {
            select: ({data}) => data
        }
    );


    return <>
        <div style={{marginBottom: '32px'}}>
            <Tabs
                data={tabs}
                active={state.activeTab}
                onChange={handleTabClick}
            />
        </div>

        {
            state.activeTab === 'overview' &&
            <div className={styles['mail']}>
                <div className={styles['mail__header']}>
                    <div className={styles['mail__header-title']}>
                        <Title weight='semibold'>{ (data?.sender?.value?.[0]?.name || data?.sender?.value?.[0]?.email)?.replaceAll('\\"', '"') }</Title>
                        <Title size={'xsmall'}>
                            &lt;{ data?.sender?.value?.[0]?.email }&gt;
                        </Title>
                    </div>
                    <div className={styles['mail__header-date']}>
                        { convertTimestampToDateAndTime(data?.sent_at) }
                    </div>
                </div>
                <div className={styles['mail__info']}>
                    <div className={styles['mail__info-item']}>
                        <div className={styles['mail__info-item-title']}>
                            <Title size={'xsmall'}>
                                Aan:
                            </Title>
                        </div>
                        <div className={styles['mail__info-item-value']}>
                            {
                                data?.receivers?.value?.map((i: any,  idx: number, arr: any) => {
                                    return <>
                                        <div className={styles['mail__info-item-value-divider']}>
                                            <div className={styles['mail__info-item-value-name']}>
                                                { i?.name || i?.email }
                                                <Title size={'xsmall'}>
                                                    &lt;{ i?.email }&gt;
                                                </Title>
                                            </div>
                                            { idx !== arr.length-1 ? ', ' : '' }
                                        </div>
                                    </>;
                                })
                            }
                        </div>
                    </div>
                    {
                        data?.cc?.value?.[0] &&
                        <div className={styles['mail__info-item']}>
                            <div className={styles['mail__info-item-title']}>
                                <Title size={'xsmall'}>
                                    Cc:
                                </Title>
                            </div>
                            <div className={styles['mail__info-item-value']}>
                                {
                                    data?.cc?.value?.map((i: any,  idx: number, arr: any) => {
                                        return <>
                                            <div className={styles['mail__info-item-value-divider']}>
                                                <div className={styles['mail__info-item-value-name']}>
                                                    { i?.name || i?.email }
                                                    <Title size={'xsmall'}>
                                                        &lt;{ i?.email }&gt;
                                                    </Title>
                                                </div>
                                                { idx !== arr.length-1 ? ', ' : '' }
                                            </div>
                                        </>;
                                    })
                                }
                            </div>
                        </div>
                    }
                </div>


                <div
                    className={styles['mail__message']}
                    dangerouslySetInnerHTML={{__html: sanitizeHtml(data?.body, {
                        allowedAttributes: {}
                    }) }}
                />

                {
                    onDelete &&
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Button
                            size='small'
                            variant='transparent'
                            color='error'
                            onClick={handleDeletePopup}
                        >
                            Verwijderen
                        </Button>
                    </div>
                }

                {
                    state.isDeletePopupOpen &&
                    <DeletionModal
                        onClose={() => setState({
                            ...state,
                            isDeletePopupOpen: false
                        })}
                        onDelete={processDelete}
                        isDeleting={deleteMutation.isLoading}
                    >
                        Als je deze mail verwijdert, wordt deze permanent verwijderd. Weet je zeker dat je deze mail wilt verwijderen?
                    </DeletionModal>
                }
            </div>
        }
        {
            state.activeTab === 'attachments' &&
            <>
                {
                    state.isPreviewing
                    ? <>
                        {
                            state.isLoadingAttachmentPreview
                            ? <Loader description={'File laden...'} />
                            : <>
                                <ButtonGroup>
                                    <Button
                                        variant='transparent'
                                        onClick={() => setState({
                                            ...state,
                                            isPreviewing: false
                                        })}
                                    >
                                        Terug
                                    </Button>
                                    <Button
                                        color='primary'
                                        variant='solid'
                                        isLoading={state.isLoading}
                                        onClick={handleDownload(state.activeAttachment)}
                                    >
                                        Downloaden
                                    </Button>
                                </ButtonGroup>
                                <br/>
                                { fileTypesWithoutPreview.includes(state.activeAttachment?.type) && <>Er kan geen voorbeeld worden getoond van dit bestandstype.</> }
                                { fileTypesAsImage.includes(state.activeAttachment?.type) && <img src={state.previewUrl} width="100%" /> }
                                { !fileTypesWithoutPreview.includes(state.activeAttachment?.type) &&
                                    !fileTypesAsImage.includes(state.activeAttachment?.type) &&
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        <iframe
                                            style={{
                                                width: '100%',
                                                height: '600px'
                                            }}
                                            src={state.previewUrl}
                                            title={state.activeAttachment.name}
                                        />
                                    </div>}
                            </>
                        }
                    </>
                    : <Table>
                        <TableHeader>
                            <TableColumn>Bestandsnaam</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {
                                data?.attachments?.map(i =>
                                    <TableRow
                                        dropdown={
                                            <Dropdown
                                                position='left'
                                                trigger={
                                                    <Button
                                                        size='large'
                                                        shape='circle'
                                                        color='neutral-dark'
                                                        iconBefore={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                    />
                                                }
                                            >
                                                <DropdownItem onClick={handleAttachmentClick(i)}>Bekijken</DropdownItem>
                                                <DropdownItem onClick={handleDownload(i)}>Downloaden</DropdownItem>
                                            </Dropdown>
                                        }
                                    >
                                        <TableCell>{i?.name}</TableCell>
                                    </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
                }
            </>
        }
        {
            state.activeTab === 'entities' &&
            Object.keys(entities).map((entityKey) => {
                return <FormGroup title={EntityHelper.getDisplayValueByName(entityKey)}>
                    <Field
                        isEdit={false}
                        type={FieldType.Multi_Selector}
                        value={entities[entityKey]}
                        fieldProperties={{
                            alignLeft: true,
                            dataset: [getInternalDatasetIdByName(entityKey)],
                        }}
                    />
                </FormGroup>;
            })
        }
    </>
}

export default Mail;